<template>
  <WebChapterTemplate
    :page-title="pageTitle"
    :section-label="sectionLabel"
    :section-route="sectionRoute"
    :top-strip-image="topStripImage"
    :hero-background-image="heroBackgroundImage"
  >
    <div class="research-gallery">
      <section
        v-for="group in groups"
        :key="group.title"
        class="gallery-section"
      >
        <div class="gallery-section__header">
          <p v-if="group.kicker" class="gallery-section__kicker">{{ group.kicker }}</p>
          <h2 class="gallery-section__title">{{ group.title }}</h2>
        </div>

        <div v-if="group.description" class="gallery-section__description">
          <p
            v-for="paragraph in normalizedDescription(group.description)"
            :key="paragraph"
          >
            {{ paragraph }}
          </p>
        </div>

        <div v-if="group.layout === 'hero-pair'" class="gallery-hero-pair">
          <figure
            v-if="group.items[0]"
            class="gallery-card gallery-card--hero"
            :class="{ 'gallery-card--contain': group.items[0].contain }"
          >
            <div class="gallery-card__image-frame">
              <img :src="group.items[0].src" :alt="group.items[0].caption" loading="lazy" />
            </div>
            <figcaption>{{ group.items[0].caption }}</figcaption>
          </figure>

          <div class="gallery-hero-pair__items">
            <figure
              v-for="item in group.items.slice(1)"
              :key="item.caption"
              class="gallery-card"
              :class="{
                'gallery-card--tall': item.tall,
                'gallery-card--contain': item.contain
              }"
            >
              <div class="gallery-card__image-frame">
                <img :src="item.src" :alt="item.caption" loading="lazy" />
              </div>
              <figcaption>{{ item.caption }}</figcaption>
            </figure>
          </div>
        </div>

        <div v-else class="gallery-grid" :class="`gallery-grid--${group.columns || 3}`">
          <figure
            v-for="item in group.items"
            :key="item.caption"
            class="gallery-card"
            :class="{
              'gallery-card--wide': item.wide,
              'gallery-card--tall': item.tall,
              'gallery-card--contain': item.contain
            }"
          >
            <div class="gallery-card__image-frame">
              <img :src="item.src" :alt="item.caption" loading="lazy" />
            </div>
            <figcaption>{{ item.caption }}</figcaption>
          </figure>
        </div>
      </section>
    </div>
  </WebChapterTemplate>
</template>

<script>
import WebChapterTemplate from '@/components/WebChapterTemplate.vue';

export default {
  name: 'ResearchGalleryPage',
  components: {
    WebChapterTemplate
  },
  props: {
    pageTitle: { type: String, required: true },
    sectionLabel: { type: String, default: '科研保障' },
    sectionRoute: { type: [String, Object], default: '/lanhu_keyanbaozhang' },
    topStripImage: { type: String, required: true },
    heroBackgroundImage: { type: String, required: true },
    groups: { type: Array, required: true }
  },
  methods: {
    normalizedDescription(description) {
      return Array.isArray(description) ? description : [description];
    }
  }
};
</script>

<style scoped>
.research-gallery {
  display: grid;
  gap: 32px;
}

.gallery-section {
  padding: 26px;
  border-radius: 12px;
  border: 1px solid rgba(179, 145, 101, 0.18);
  background:
    linear-gradient(140deg, rgba(248, 242, 233, 0.84), rgba(255, 255, 255, 0) 42%),
    linear-gradient(180deg, #fff, rgba(253, 250, 246, 0.96));
  box-shadow: 0 14px 30px rgba(59, 41, 17, 0.05);
}

.gallery-section__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 22px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(179, 145, 101, 0.16);
}

.gallery-section__kicker {
  margin: 0;
  color: #b28a56;
  font-size: 12px;
  line-height: 1.4;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.gallery-section__title {
  margin: 0;
  color: #f25c19;
  font-size: 26px;
  line-height: 1.4;
  font-family: 'Songti SC', 'STSong', 'Noto Serif SC', serif;
  font-weight: 700;
}

.gallery-section__description {
  margin: -4px 0 24px;
  color: #2f2a25;
  font-size: 16px;
  line-height: 2;
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.gallery-section__description p {
  margin: 0 0 10px;
  text-align: justify;
  text-indent: 2em;
}

.gallery-section__description p:last-child {
  margin-bottom: 0;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
}

.gallery-grid--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.gallery-grid--1 {
  grid-template-columns: 1fr;
}

.gallery-grid--4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.gallery-grid--5 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.gallery-hero-pair {
  display: grid;
  gap: 20px;
}

.gallery-hero-pair__items {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.gallery-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(179, 145, 101, 0.16);
  box-shadow: 0 10px 24px rgba(45, 33, 16, 0.05);
}

.gallery-card--wide {
  grid-column: span 2;
}

.gallery-card--tall .gallery-card__image-frame {
  aspect-ratio: auto;
}

.gallery-card--hero .gallery-card__image-frame {
  aspect-ratio: auto;
}

.gallery-card__image-frame {
  width: 100%;
  background: #f6f2ec;
  overflow: hidden;
}

.gallery-card__image-frame img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

.gallery-card figcaption {
  margin: 0;
  padding: 14px 16px 16px;
  color: #4e463d;
  font-size: 14px;
  line-height: 1.8;
  text-align: justify;
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

@media (max-width: 1024px) {
  .gallery-grid,
  .gallery-grid--2,
  .gallery-grid--4,
  .gallery-grid--5,
  .gallery-hero-pair__items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .gallery-card--hero .gallery-card__image-frame {
    aspect-ratio: auto;
  }
}

@media (max-width: 768px) {
  .gallery-section {
    padding: 20px;
  }

  .gallery-section__header {
    display: block;
  }

  .gallery-section__title {
    font-size: 22px;
  }

  .gallery-grid,
  .gallery-grid--2,
  .gallery-grid--4,
  .gallery-grid--5,
  .gallery-hero-pair__items {
    grid-template-columns: 1fr;
  }

  .gallery-card--wide {
    grid-column: auto;
  }
}
</style>
